import React, { useEffect, useRef, useState } from 'react';
import { Target, Zap, ShieldCheck, BookOpen, MapPin, Briefcase } from 'lucide-react';
import { aboutData, experienceData, educationData } from '../data/mock';
import { Badge } from './ui/badge';

function useInView(ref) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
  return visible;
}

export default function AboutSection() {
  const sectionRef = useRef(null);
  const visible = useInView(sectionRef);

  return (
    <section id="about" className="relative" ref={sectionRef}>
      <div className="section-container">
        {/* About */}
        <div className={`fade-in-up ${visible ? 'visible' : ''}`}>
          <p className="section-label">// About Me</p>
          <h2 className="section-title">Building Cyber-Resilient Futures</h2>
          <div className="glow-line w-20 mb-10" />
        </div>

        <div className={`grid md:grid-cols-5 gap-12 mb-24 fade-in-up ${visible ? 'visible' : ''}`} style={{ transitionDelay: '0.1s' }}>
          {/* Left - narrative */}
          <div className="md:col-span-3 space-y-5">
            <p className="text-[#c8d1dc] leading-relaxed text-[15px]">
              {aboutData.summary}
            </p>
            <p className="text-[#94a3b8] leading-relaxed text-[15px]">
              {aboutData.highlight}
            </p>
            <div className="pt-4 border-t border-[#1e293b]/60">
              <p className="text-[#00e5ff] font-['JetBrains_Mono'] text-sm italic">
                "{aboutData.mission}"
              </p>
            </div>
          </div>

          {/* Right - strengths */}
          <div className="md:col-span-2">
            <div className="cyber-card p-6">
              <h3 className="font-['JetBrains_Mono'] text-xs text-[#00e5ff] tracking-widest uppercase mb-5 flex items-center gap-2">
                <Zap className="w-3.5 h-3.5" /> Core Strengths
              </h3>
              <div className="flex flex-wrap gap-2">
                {aboutData.strengths.map((s) => (
                  <Badge
                    key={s}
                    variant="outline"
                    className="border-[#1e293b] text-[#94a3b8] hover:border-[#00e5ff]/40 hover:text-[#00e5ff] transition-colors duration-200 bg-transparent text-xs py-1.5"
                  >
                    {s}
                  </Badge>
                ))}
              </div>
              <div className="mt-6 pt-5 border-t border-[#1e293b]/60">
                <p className="text-[#64748b] text-xs leading-relaxed">
                  {aboutData.philosophy}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Experience Timeline */}
        <div id="experience" className={`fade-in-up ${visible ? 'visible' : ''}`} style={{ transitionDelay: '0.2s' }}>
          <p className="section-label">// Career Timeline</p>
          <h2 className="section-title text-3xl">Professional Journey</h2>
          <div className="glow-line w-20 mb-10" />
        </div>

        <div className="relative pl-14">
          <div className="timeline-line" />

          {experienceData.map((exp, idx) => (
            <div
              key={exp.id}
              className={`relative mb-10 last:mb-0 fade-in-up ${visible ? 'visible' : ''}`}
              style={{ transitionDelay: `${0.3 + idx * 0.1}s` }}
            >
              <div className={`timeline-dot ${exp.current ? 'active' : ''}`} />
              <div className="cyber-card p-5 ml-2 hover:translate-x-1 transition-transform duration-300">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h3 className="text-[15px] font-semibold text-[#f1f5f9]">{exp.role}</h3>
                  {exp.current && (
                    <Badge className="bg-[#00ff88]/10 text-[#00ff88] border-[#00ff88]/30 text-[10px] font-['JetBrains_Mono'] hover:bg-[#00ff88]/20">
                      Current
                    </Badge>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-4 mb-2">
                  <span className="flex items-center gap-1.5 text-sm text-[#00e5ff]">
                    <Briefcase className="w-3.5 h-3.5" /> {exp.company}
                  </span>
                  <span className="text-xs text-[#64748b] font-['JetBrains_Mono']">{exp.period}</span>
                  {exp.location && (
                    <span className="flex items-center gap-1 text-xs text-[#64748b]">
                      <MapPin className="w-3 h-3" /> {exp.location}
                    </span>
                  )}
                </div>
                <p className="text-sm text-[#94a3b8] leading-relaxed">{exp.description}</p>
                {exp.bullets && exp.bullets.length > 0 && (
                  <ul className="mt-3 space-y-1.5">
                    {exp.bullets.map((bullet, bIdx) => (
                      <li key={bIdx} className="text-xs text-[#7a8ba3] leading-relaxed flex gap-2">
                        <span className="text-[#00e5ff] mt-0.5 flex-shrink-0">▹</span>
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Education */}
        <div className={`mt-16 fade-in-up ${visible ? 'visible' : ''}`} style={{ transitionDelay: '0.5s' }}>
          <div className="flex items-center gap-3 mb-6">
            <BookOpen className="w-4 h-4 text-[#00e5ff]" />
            <h3 className="font-['JetBrains_Mono'] text-xs text-[#00e5ff] tracking-widest uppercase">Education</h3>
          </div>
          {educationData.map((edu) => (
            <div key={edu.degree} className="cyber-card p-5">
              <h4 className="text-[15px] font-semibold text-[#f1f5f9] mb-1">{edu.degree}</h4>
              <div className="flex flex-wrap items-center gap-3">
                <span className="flex items-center gap-1.5 text-sm text-[#94a3b8]">
                  <MapPin className="w-3.5 h-3.5 text-[#64748b]" /> {edu.institution}
                </span>
                <span className="text-xs text-[#64748b] font-['JetBrains_Mono']">{edu.period}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
